import React, { useCallback, useState } from 'react';
import {
  Box, Button, Container, Paper, TextField, Typography,
  Link, Alert, InputAdornment, IconButton, Stack
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useMutation } from '@apollo/client';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { LOGIN_MUTATION, ME_QUERY } from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import apolloClient from '../graphql/client';
import LegalFooter from '../components/LegalFooter';
import LegalAcceptanceGate from '../components/LegalAcceptanceGate';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  // Signed in, but documents to accept first (first sign-in of an account an admin created, new versions…)
  const [pendingLogin, setPendingLogin] = useState(null); // { token, me }

  const [loginMutation, { loading }] = useMutation(LOGIN_MUTATION);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const { data } = await loginMutation({ variables: { email: form.email, password: form.password } });
      const token = data.login;
      localStorage.setItem('token', token);   // set BEFORE ME_QUERY so auth header is sent
      const { data: meData } = await apolloClient.query({ query: ME_QUERY, fetchPolicy: 'network-only' });
      setPendingLogin({ token, me: meData.me }); // the dashboard opens once the documents are accepted
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    }
  };

  const enter = useCallback(() => {
    login(pendingLogin.token, pendingLogin.me);
    navigate('/dashboard');
  }, [login, navigate, pendingLogin]);
  const cancelLogin = () => {
    localStorage.removeItem('token');
    apolloClient.clearStore();
    setPendingLogin(null);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #4a0072 0%, #ae52d4 100%)',
      display: 'flex',
      alignItems: 'center',
    }}>
      <Container maxWidth="xs">
        <Paper elevation={6} sx={{ p: 5, borderRadius: 4 }}>
          <Stack alignItems="center" spacing={1} mb={4}>
            <FavoriteIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            <Typography variant="h5" fontWeight={700}>Welcome Back</Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to continue your journey
            </Typography>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 3 }}
            />
            <TextField
              label="Password"
              name="password"
              type={showPwd ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              fullWidth
              required
              sx={{ mb: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPwd(p => !p)} edge="end">
                      {showPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ textAlign: 'right', mb: 3 }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                Forgot password?
              </Link>
            </Box>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={loading || Boolean(pendingLogin)}
              sx={{ fontWeight: 700, py: 1.5 }}
            >
              {loading || pendingLogin ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <Typography variant="body2" align="center" sx={{ mt: 3 }}>
            Don't have an account?{' '}
            <Link component={RouterLink} to="/signup" fontWeight={600}>
              Create one
            </Link>
          </Typography>
          <Typography variant="body2" align="center" sx={{ mt: 1 }}>
            <Link component={RouterLink} to="/" color="text.secondary">
              Back to Home
            </Link>
          </Typography>
        </Paper>
        <LegalFooter dark sx={{ mt: 2 }} />
      </Container>
      {pendingLogin && <LegalAcceptanceGate onDone={enter} onLogout={cancelLogin} />}
    </Box>
  );
}
